import { useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  getStoredAcquisition,
  getStoredLinkedThumbmark,
  hasAcquisitionData,
  persistAcquisitionFromUrl,
  persistLinkedThumbmarkFromUrl,
} from '@/lib/utils/acquisition-tracking';
import { getCometToken, setUserCometToken } from '@/lib/analytics/cometly';

type ClerkUser = NonNullable<ReturnType<typeof useUser>['user']>;

/** Same options as writeThumbmarkToUser / UserDeviceTracker — one device, one value. */
async function computeThumbmark(): Promise<string | null> {
  try {
    const { Thumbmark } = await import('@thumbmarkjs/thumbmarkjs');
    const tm = new Thumbmark({ logging: false, exclude: ['system.applePayVersion'] });
    const { thumbmark } = await tm.get();
    return thumbmark ?? null;
  } catch {
    return null;
  }
}

/**
 * `unsafeMetadata` to attach at sign-up creation — `signUp.create(...)` AND
 * `signUp.authenticateWithRedirect(...)` (Clerk carries it through the OAuth
 * bounce and stamps it on the new user). This is what makes the acquisition
 * visible to the CRM admin's `on-org-created` even when the org is created
 * before any post-login `user.update` could run: on Google sign-ups the
 * write used to happen after the redirect, and the org webhook won the race
 * (21 utm_source on 2 598 signups in 90 days — TAG-2514).
 *
 * Returns `{}` when there is nothing to carry so callers can spread it.
 */
export async function buildSignUpUnsafeMetadata(): Promise<{
  unsafeMetadata?: Record<string, unknown>;
}> {
  const meta: Record<string, unknown> = {};
  const acquisition = getStoredAcquisition();
  if (hasAcquisitionData(acquisition)) meta.acquisition = acquisition;
  const linked = getStoredLinkedThumbmark();
  if (linked) meta.device_thumbmark_linked = linked;
  const thumbmark = await computeThumbmark();
  if (thumbmark) meta.device_thumbmark = thumbmark;
  // Cometly journey token → CRM Sign Up attribution (admin `crm-on-org-created`).
  const cometToken = getCometToken();
  if (cometToken) meta.comet_token = cometToken;
  return Object.keys(meta).length > 0 ? { unsafeMetadata: meta } : {};
}

/**
 * Write the session's first-touch acquisition to Clerk
 * `user.unsafeMetadata.acquisition` — the carrier the CRM admin reads off the
 * creator user when their organization is created.
 *
 * Strict first-touch: an existing acquisition object (on `unsafeMetadata`, or
 * legacy `publicMetadata` for accounts created before the FE-write policy
 * fix) is NEVER overwritten.
 *
 * IMPORTANT: writes go to `unsafeMetadata`, NOT `publicMetadata`. The
 * Frontend API rejects `publicMetadata` writes (Clerk security model: only
 * the Backend API can mutate `publicMetadata`).
 */
export async function writeAcquisitionToUser(
  user: ClerkUser | null | undefined,
): Promise<void> {
  if (!user) return;

  const existing =
    (user.unsafeMetadata as Record<string, unknown> | undefined)?.acquisition ??
    (user.publicMetadata as Record<string, unknown> | undefined)?.acquisition;
  if (existing) return;

  const acquisition = getStoredAcquisition();
  if (!hasAcquisitionData(acquisition)) return;

  try {
    await user.update({
      unsafeMetadata: {
        ...(user.unsafeMetadata as Record<string, unknown> | undefined),
        acquisition,
      },
    });
  } catch (error) {
    console.error('Failed to store acquisition data on user metadata:', error);
  }
}

/**
 * ThumbmarkJS device thumbmark for ad-touch stitching. Options MUST stay
 * identical to UserDeviceTracker.tsx (and payment-stack surfaces) so the
 * same device produces the same value everywhere. Written once to
 * `unsafeMetadata.device_thumbmark`; the CRM admin's `on-org-created` lands
 * it in `clients.metadata.device_thumbmark` where the ad-touch stitcher can
 * match it against paid anonymous touches from the landing pages — even for
 * signups that never open a dashboard (no login-time signal yet).
 */
export async function writeThumbmarkToUser(
  user: ClerkUser | null | undefined,
): Promise<void> {
  if (!user) return;
  if ((user.unsafeMetadata as Record<string, unknown> | undefined)?.device_thumbmark) return;

  try {
    const thumbmark = await computeThumbmark();
    if (!thumbmark) return;

    await user.update({
      unsafeMetadata: {
        ...(user.unsafeMetadata as Record<string, unknown> | undefined),
        device_thumbmark: thumbmark,
      },
    });
  } catch (error) {
    console.error('Failed to store device thumbmark on user metadata:', error);
  }
}

/**
 * Cross-domain linker mark: `?tgd_tm=` carried by decorated tagada.io links
 * (see acquisition-tracking.ts). Written once to
 * `unsafeMetadata.device_thumbmark_linked` — may differ from this device's
 * own thumbmark when the click hopped browsers (in-app WebView → system).
 */
export async function writeLinkedThumbmarkToUser(
  user: ClerkUser | null | undefined,
): Promise<void> {
  if (!user) return;
  if ((user.unsafeMetadata as Record<string, unknown> | undefined)?.device_thumbmark_linked)
    return;

  const linked = getStoredLinkedThumbmark();
  if (!linked) return;

  try {
    await user.update({
      unsafeMetadata: {
        ...(user.unsafeMetadata as Record<string, unknown> | undefined),
        device_thumbmark_linked: linked,
      },
    });
  } catch (error) {
    console.error('Failed to store linked thumbmark on user metadata:', error);
  }
}

/**
 * Capture first-touch acquisition (UTM params, ref, referrer, landing page)
 * into sessionStorage and persist it to the signed-in user's Clerk metadata,
 * along with the device thumbmark (ad-touch stitching).
 * Mounted app-wide (App.tsx) and on the auth pages; idempotent — the
 * first-touch guards make extra mounts harmless.
 */
export function useAcquisitionTracking() {
  const { user, isLoaded } = useUser();
  const { actor } = useAuth();
  const wroteRef = useRef(false);

  // The sign-up journey rides on every link to the TagadaPay dashboard
  // (`lib/tagadapay-dashboard.ts`); never under "Sign in as user".
  const signUpCometToken = (user?.unsafeMetadata as Record<string, unknown> | undefined)?.comet_token;
  useEffect(() => {
    setUserCometToken(isLoaded && user && !actor ? signUpCometToken : null);
  }, [isLoaded, user, actor, signUpCometToken]);

  useEffect(() => {
    persistAcquisitionFromUrl();
    persistLinkedThumbmarkFromUrl();

    if (!isLoaded || !user || wroteRef.current) return;
    wroteRef.current = true;
    void (async () => {
      // Sequential: two concurrent user.update calls would race and one
      // metadata write would be lost (Clerk replaces the whole object).
      await writeAcquisitionToUser(user);
      await writeThumbmarkToUser(user);
      await writeLinkedThumbmarkToUser(user);
    })();
  }, [user, isLoaded]);
}

/**
 * Acquisition payload for organization publicMetadata, sent through the
 * backend `organizations.updateAcquisitionMetadata` mutation (whose schema
 * requires `timestamp`). Kept separate from the user-metadata carrier above,
 * which stays capped to the whitelisted attribution keys.
 *
 * Prioritizes: sessionStorage first-touch > user unsafeMetadata > legacy
 * publicMetadata.
 */
export function getAcquisitionMetadataForOrganization(user?: any): Record<string, any> {
  const stored = getStoredAcquisition();
  const acquisition = hasAcquisitionData(stored)
    ? stored
    : user?.unsafeMetadata?.acquisition ?? user?.publicMetadata?.acquisition;

  const base = acquisition && typeof acquisition === 'object' ? acquisition : {};
  return {
    acquisition: {
      ...base,
      timestamp: new Date().toISOString(),
    },
  };
}
